import React, { useState, useEffect } from "react";
import useFetchObjects from "../../api/useFetchObjects";
import { useAuth } from "../../context/AuthContext";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Button,
  TextField,
  InputAdornment,
  Skeleton,
  Pagination,
  MenuItem,
  FormControl,
  Select,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import PrintIcon from "@mui/icons-material/Print";
import ErrorPage from "../Components/ErrorPage";
import PayrollAdd from "./PayrollAdd";
import PayrollEdit from "./PayrollEdit";
import useDelete from "../../api/useDelete";
import { useTranslation } from "react-i18next";
import { printPayroll } from "../utils/printPayroll";

const PayrollIndex = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token;

  // Pagination and filtering states
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1); // one-based indexing
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [selectedEmployee, setSelectedEmployee] = useState("");
  const [selectedYear, setSelectedYear] = useState("");
  const [selectedMonth, setSelectedMonth] = useState("");

  // Modal states
  const [openAdd, setOpenAdd] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [editData, setEditData] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, id: null });

  // Fetch payroll records with search and pagination.
  const { data, isLoading, isError, refetch } = useFetchObjects(
    [
      "payroll",
      searchTerm,
      selectedEmployee,
      selectedYear,
      selectedMonth,
      page,
      rowsPerPage,
    ],
    `shop/payrolls/?search=${searchTerm}&employee=${selectedEmployee}&year=${selectedYear}&month=${selectedMonth}&page=${page}&page_size=${rowsPerPage}`,
    token
  );

  // Fetch employees for dropdown filter
  const { data: employeesData } = useFetchObjects(
    ["employees"],
    "shop/employees/?page_size=1000",
    token
  );

  // Delete hook
  const { deleteObject } = useDelete();

  // Handle delete function
  const handleDelete = (id) => {
    setConfirmDialog({ open: true, id });
  };

  const confirmDelete = async () => {
    try {
      await deleteObject(`shop/payrolls/${confirmDialog.id}/`, token);
      refetch();
      setConfirmDialog({ open: false, id: null });
    } catch (error) {
      console.error('Delete failed:', error);
    }
  };

  // Print function for individual payroll
  const handlePrintPayroll = (payroll) => {
    const printWindow = window.open('', '_blank');
    const printContent = `
      <html>
        <head>
          <title>Payroll Receipt</title>
          <style>
            @media print {
              @page { margin: 0; size: 80mm auto; }
            }
            body { 
              font-family: 'Courier New', monospace; 
              margin: 0; 
              padding: 5mm;
              width: 70mm;
              font-size: 12px;
              line-height: 1.2;
            }
            .center { text-align: center; }
            .bold { font-weight: bold; }
            .line { border-bottom: 1px dashed #000; margin: 3px 0; }
            .row { display: flex; justify-content: space-between; margin: 2px 0; }
            .total { font-size: 14px; font-weight: bold; }
          </style>
        </head>
        <body>
          <div class="center bold">
            <div>PAYROLL RECEIPT</div>
            <div class="line"></div>
          </div>
          
          <div class="row">
            <span>Date:</span>
            <span>${new Date().toLocaleDateString()}</span>
          </div>
          
          <div class="line"></div>
          
          <div class="row">
            <span>Employee:</span>
            <span>${payroll?.employee_details?.name || 'N/A'}</span>
          </div>
          
          <div class="row">
            <span>Shop:</span>
            <span>${payroll?.shop_details?.name || 'N/A'}</span>
          </div>
          
          <div class="row">
            <span>Period:</span>
            <span>${payroll.month || 'N/A'}/${payroll.year || 'N/A'}</span>
          </div>
          
          <div class="line"></div>
          
          <div class="row total">
            <span>AMOUNT:</span>
            <span>${payroll.amount || '0'} ${payroll?.currency_details?.title || ''}</span>
          </div>
          
          <div class="line"></div>
          
          <div class="row">
            <span>Payment Date:</span>
            <span>${payroll.payment_date ? new Date(payroll.payment_date).toLocaleDateString() : 'N/A'}</span>
          </div>
          
          ${payroll.notes ? `<div class="row"><span>Notes:</span><span>${payroll.notes}</span></div>` : ''}
          
          <div class="line"></div>
          <div class="center" style="margin-top: 10px; font-size: 10px;">
            Thank you!
          </div>
        </body>
      </html>
    `;
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.print();
  };

  useEffect(() => {
    refetch();
  }, [
    searchTerm,
    selectedEmployee,
    selectedYear,
    selectedMonth,
    page,
    rowsPerPage,
    refetch,
  ]);

  // Determine payroll list and total count (using DRF paginated response)
  const payrolls = data && data.results ? data.results : data || [];
  const totalCount = data && data.count ? data.count : payrolls.length;

  if (isError) {
    return <ErrorPage message={t("payroll.failedToLoadPayrolls")} />;
  }

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setPage(1);
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (e) => {
    setRowsPerPage(Number(e.target.value));
    setPage(1);
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "stretch", sm: "center" },
          mb: 2,
          gap: 2,
        }}
      >
        <Typography variant="h4">{t("payroll.payrollList")}</Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={() => setOpenAdd(true)}
        >
          {t("payroll.addPayroll")}
        </Button>
      </Box>
      <Typography variant="subtitle1" color="textSecondary" mb={2}>
        {t("payroll.dataLoadedOn")} {new Date().toLocaleString()}
      </Typography>
      {/* Search and Filter Fields */}
      <Box sx={{ mb: 2, display: "flex", gap: 2 }}>
        <TextField
          variant="outlined"
          fullWidth
          placeholder={t("payroll.searchPayrolls")}
          value={searchTerm}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
        <FormControl fullWidth variant="outlined">
          <Select
            value={selectedEmployee}
            onChange={(e) => setSelectedEmployee(e.target.value)}
            displayEmpty
            placeholder={t("payroll.selectEmployee")}
          >
            <MenuItem value="">{t("payroll.selectEmployee")}</MenuItem>
            {(employeesData?.results || employeesData || []).map((employee) => (
              <MenuItem key={employee.id} value={employee.id}>
                {employee.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Payroll Table */}
      <TableContainer component={Paper} sx={{ mt: 2 }}>
        <Table sx={{ minWidth: 750 }} aria-label="payroll table">
          <TableHead>
            <TableRow sx={{ backgroundColor: "primary.main" }}>
              <TableCell sx={{ color: "white" }}>
                {t("payroll.payrollId")}
              </TableCell>
              <TableCell sx={{ color: "white" }}>{t("payroll.shop")}</TableCell>
              <TableCell sx={{ color: "white" }}>
                {t("payroll.employee")}
              </TableCell>
              <TableCell sx={{ color: "white" }}>
                {t("payroll.amount")}
              </TableCell>
              <TableCell sx={{ color: "white" }}>
                {t("payroll.currency")}
              </TableCell>
              <TableCell sx={{ color: "white" }}>{t("payroll.year")}</TableCell>
              <TableCell sx={{ color: "white" }}>
                {t("payroll.month")}
              </TableCell>
              <TableCell sx={{ color: "white" }}>
                {t("payroll.paymentDate")}
              </TableCell>
              <TableCell sx={{ color: "white" }}>
                {t("payroll.notes")}
              </TableCell>
              <TableCell sx={{ color: "white" }}>
                {t("payroll.action")}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading
              ? Array.from(new Array(5)).map((_, rowIndex) => (
                  <TableRow key={`skeleton-${rowIndex}`}>
                    {[
                      "ID",
                      "Shop",
                      "Employee",
                      "Amount",
                      "Currency",
                      "Year",
                      "Month",
                      "Payment Date",
                      "Notes",
                      "Action",
                    ].map((_, colIndex) => (
                      <TableCell key={`skeleton-${rowIndex}-${colIndex}`}>
                        <Skeleton variant="text" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              : payrolls.map((payroll, index) => (
                  <TableRow key={payroll.id} hover>
                    <TableCell>
                      {(page - 1) * rowsPerPage + index + 1}
                    </TableCell>
                    <TableCell>
                      {payroll?.shop_details?.name || t("payroll.unknownShop")}
                    </TableCell>
                    <TableCell>
                      {payroll?.employee_details?.name ||
                        t("payroll.unknownEmployee")}
                    </TableCell>
                    <TableCell>{payroll.amount || "--"}</TableCell>
                    <TableCell>
                      {payroll?.currency_details?.title || "--"}
                    </TableCell>
                    <TableCell>{payroll.year || "--"}</TableCell>
                    <TableCell>{payroll.month || "--"}</TableCell>
                    <TableCell>
                      {payroll.payment_date
                        ? new Date(payroll.payment_date).toLocaleString()
                        : "--"}
                    </TableCell>
                    <TableCell>{payroll.notes || "--"}</TableCell>
                    <TableCell>
                      <Button
                        variant="outlined"
                        size="small"
                        sx={{ mr: 1 }}
                        onClick={() => {
                          setEditData(payroll);
                          setOpenEdit(true);
                        }}
                      >
                        {t("payroll.editPayroll")}
                      </Button>
                      <Button
                        variant="outlined"
                        color="primary"
                        size="small"
                        sx={{ mr: 1 }}
                        startIcon={<PrintIcon />}
                        onClick={() => handlePrintPayroll(payroll)}
                      >
                        Print
                      </Button>
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        onClick={() => handleDelete(payroll.id)}
                      >
                        {t("payroll.delete")}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <Grid
        container
        justifyContent="center"
        alignItems="center"
        padding="10px"
      >
        <Box display="flex" alignItems="center">
          <Typography>{t("payroll.rowsPerPage", "Rows Per Page")}</Typography>
          <FormControl
            sx={{
              "& .MuiOutlinedInput-root": {
                "& fieldset": {
                  border: "none",
                },
              },
            }}
          >
            <Select value={rowsPerPage} onChange={handleRowsPerPageChange}>
              <MenuItem value={10}>10</MenuItem>
              <MenuItem value={20}>20</MenuItem>
              <MenuItem value={30}>30</MenuItem>
            </Select>
          </FormControl>
        </Box>
        <Pagination
          count={Math.ceil(totalCount / rowsPerPage)}
          page={page}
          onChange={handlePageChange}
          variant="outlined"
          color="primary"
        />
      </Grid>

      {openAdd && (
        <PayrollAdd
          open={openAdd}
          onClose={() => setOpenAdd(false)}
          token={token}
        />
      )}

      {openEdit && (
        <PayrollEdit
          open={openEdit}
          onClose={() => setOpenEdit(false)}
          editData={editData}
          token={token}
        />
      )}

      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ open: false, id: null })}
      >
        <DialogTitle>{t("payroll.confirmDelete")}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t("payroll.confirmDeleteMessage")}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog({ open: false, id: null })}>
            {t("payroll.cancel")}
          </Button>
          <Button onClick={confirmDelete} color="error" autoFocus>
            {t("payroll.delete")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PayrollIndex;
